/**
 * CCTV 상세 — 맵에서 CCTV 를 누르면 연다. 휴대폰에서는 전체 화면.
 *  - 배경(디워핑) 이미지 위에 ROI 를 그리고, 카메라 판정(redis cctv:<name>)으로 칠한다
 *  - 연결 주차면마다 DB 점유 / 카메라 판정 / ROI 유무 — 어긋나면 표시
 *  - 카메라 판정이 얼마나 최근인지 (router 는 30초 넘으면 끊김 처리)
 *  - 이 CCTV 전체 점유/비움 (만공차등 만차/공차 확인용)
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close as CloseIcon, Refresh as RefreshIcon } from '@mui/icons-material';

import {
  QaCctvDetail,
  QaCctvLights,
  QaDetection,
  QaSpace,
  SiteParkingQaService,
} from '../../services/SiteParkingQaService';
import { SPACE_COLOR } from './QaSiteMap';

/** 'ParkingLocations_9' → '9' — 좁은 화면에서 표·ROI 에 짧게 쓴다 (전체 이름은 title). */
export const shortSpaceName = (name: string) => name.replace(/^parkinglocations[-_]/i, '') || name;

/** router parking.service MAX_STALENESS_SECONDS — 이보다 오래된 판정이면 CCTV 를 끊김으로 본다. */
const STALE_SECONDS = 30;

interface Props {
  projectId: number;
  ip: string;
  /** 연 CCTV 이름. null 이면 닫힘. */
  name: string | null;
  /** 이 CCTV 주차면의 DB 상태 (화면이 들고 있는 최신 값 — 바꾸면 바로 반영된다). */
  spaces: QaSpace[];
  held: boolean;
  pending: Set<number>;
  onToggle: (space: QaSpace) => void;
  onApplyAll: (ids: number[], status: 'Y' | 'N') => void;
  onClose: () => void;
  /** 자동 갱신으로 받은 이 CCTV 의 최신 카메라 판정 — 상세를 연 뒤에도 계속 바뀐다. */
  liveDetection?: QaDetection;
  liveNow?: number;
  /** 이 CCTV 에 달린 통로등·만공차등 (자동 갱신). */
  lights?: QaCctvLights;
}

const QaCctvDialog: React.FC<Props> = ({
  projectId,
  ip,
  name,
  spaces,
  held,
  pending,
  onToggle,
  onApplyAll,
  onClose,
  liveDetection,
  liveNow,
  lights,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [detail, setDetail] = useState<QaCctvDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [imgSize, setImgSize] = useState<{ w: number; h: number } | null>(null);

  const load = useCallback(async () => {
    if (!name) return;
    setLoading(true);
    setError(null);
    try {
      setDetail(await SiteParkingQaService.cctvDetail(projectId, ip, name));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'CCTV 정보 조회 실패');
    } finally {
      setLoading(false);
    }
  }, [projectId, ip, name]);

  useEffect(() => {
    setDetail(null);
    setImgSize(null);
    load();
  }, [load]);

  // 상세를 연 뒤에는 자동 갱신 판정이 더 최신이면 그것을 쓴다.
  const useLive =
    !!liveDetection &&
    liveNow !== undefined &&
    (!detail?.detection || liveDetection.timestamp >= detail.detection.timestamp);
  const detection = useLive ? liveDetection : (detail?.detection ?? null);
  const detectionNow = useLive ? (liveNow as number) : (detail?.now ?? 0);
  const camera = useMemo(
    () => new Map((detection?.parking ?? []).map((p) => [p.id, p.status])),
    [detection],
  );
  const roiNames = useMemo(() => new Set((detail?.rois ?? []).map((r) => r.parking)), [detail]);
  const age = detection ? detectionNow - detection.timestamp : null;
  const ids = spaces.map((s) => s.id);
  const full = spaces.length > 0 && spaces.every((s) => s.status === 'Y');
  const busy = ids.some((id) => pending.has(id));
  const viewW = imgSize?.w ?? 640;
  const viewH = imgSize?.h ?? 640;

  return (
    <Dialog
      open={!!name}
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="md"
      aria-labelledby="qa-cctv-dialog-title"
      sx={
        fullScreen
          ? {
              height: '100dvh',
              bottom: 'auto',
              '& .MuiDialog-paper': {
                height: '100%',
                maxHeight: '100%',
                boxSizing: 'border-box',
                pt: 'env(safe-area-inset-top, 0px)',
                pb: 'env(safe-area-inset-bottom, 0px)',
              },
            }
          : undefined
      }
      data-testid="qa-cctv-dialog"
    >
      <DialogTitle
        id="qa-cctv-dialog-title"
        sx={{ position: 'relative', flexShrink: 0, px: { xs: 2, sm: 3 }, pr: 8, pb: 1 }}
      >
        <Typography
          variant="h6"
          component="span"
          fontWeight={700}
          sx={{ overflowWrap: 'anywhere' }}
        >
          {name}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
          {detail ? `${detail.ip}${detail.server ? ` · 영상처리서버 ${detail.server}` : ''}` : ip}
        </Typography>
        <IconButton
          aria-label="닫기"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8, width: 48, height: 48 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          minHeight: 0,
          overflowY: 'auto',
          overscrollBehaviorY: 'contain',
          px: { xs: 1.5, sm: 3 },
        }}
      >
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
          <Chip
            size="small"
            color={full ? 'error' : 'success'}
            label={full ? '만공차등 만차(소등)' : '만공차등 공차(점등)'}
          />
          {(held || (detail?.holdSeconds ?? 0) > 0) && (
            <Chip
              size="small"
              color="warning"
              label={`카메라 반영 중지${detail?.holdSeconds ? ` · ${Math.ceil(detail.holdSeconds / 60)}분 남음` : ''}`}
            />
          )}
          {detail &&
            (age === null ? (
              <Chip
                size="small"
                color="error"
                variant="outlined"
                label="카메라 판정 없음 (영상처리 미동작)"
              />
            ) : (
              <Chip
                size="small"
                variant="outlined"
                color={age <= STALE_SECONDS ? 'success' : 'error'}
                label={
                  age <= STALE_SECONDS
                    ? `카메라 판정 ${Math.max(age, 0)}초 전`
                    : `카메라 판정 ${age}초 전 — 끊김으로 처리됨`
                }
                data-testid="qa-cctv-age"
              />
            ))}
        </Stack>

        {lights && (lights.aisleTotal > 0 || lights.parkingTotal > 0) && (
          <Box sx={{ mb: 1.5 }} data-testid="qa-cctv-lights">
            <Typography variant="body2" fontWeight={700}>
              조명
              <Typography component="span" variant="body2" color="text.secondary">
                {lights.aisleTotal > 0 &&
                  ` · 통로등 ${lights.aisleOn}/${lights.aisleTotal}${lights.aisleOn ? ` ${lights.aisleValue}%` : ' 꺼짐'}`}
                {lights.parkingTotal > 0 &&
                  ` · 만공차등 ${lights.parkingOn}/${lights.parkingTotal}${lights.parkingOn ? ` ${lights.parkingValue}%` : ' 꺼짐'}`}
              </Typography>
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
              {[...lights.nodes]
                .sort((a, b) =>
                  a.kind === b.kind ? a.modId - b.modId : a.kind === 'aisle' ? -1 : 1,
                )
                .map((n) => (
                  <Chip
                    key={`${n.kind}-${n.modId}`}
                    size="small"
                    variant={n.value > 0 ? 'filled' : 'outlined'}
                    label={`${n.kind === 'aisle' ? '통로등' : '만공차등'} ${n.modId} · ${n.value > 0 ? `${n.value}%` : '꺼짐'}`}
                    sx={
                      n.value > 0
                        ? {
                            bgcolor: n.kind === 'aisle' ? '#ffd23f' : '#22c55e',
                            color: '#111',
                            fontWeight: 700,
                          }
                        : undefined
                    }
                  />
                ))}
            </Stack>
          </Box>
        )}

        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            color="error"
            disabled={!ids.length || busy}
            onClick={() => onApplyAll(ids, 'Y')}
            sx={{ flex: 1, minHeight: 40 }}
          >
            전체 점유 (만차)
          </Button>
          <Button
            variant="outlined"
            color="success"
            disabled={!ids.length || busy}
            onClick={() => onApplyAll(ids, 'N')}
            sx={{ flex: 1, minHeight: 40 }}
          >
            전체 비움 (공차)
          </Button>
          <IconButton aria-label="새로고침" onClick={load} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading && !detail && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {detail && (
          <>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              ROI · 카메라 판정
            </Typography>
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                maxWidth: 520,
                mx: 'auto',
                aspectRatio: `${viewW} / ${viewH}`,
                bgcolor: '#0d1117',
                borderRadius: 1,
                overflow: 'hidden',
                mb: 0.5,
              }}
            >
              {(detail.liveImage || detail.image) && (
                <img
                  src={`data:image/jpeg;base64,${detail.liveImage || detail.image}`}
                  alt={`${name} ${detail.liveImage ? '실시간 판정' : '배경'}`}
                  onLoad={(e) =>
                    setImgSize({
                      w: e.currentTarget.naturalWidth,
                      h: e.currentTarget.naturalHeight,
                    })
                  }
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
                />
              )}
              {/* 실시간 판정 이미지는 카메라가 ROI·비율을 이미 그려 넣은 화면이라 겹치지 않는다. */}
              {!detail.liveImage && (
                <svg
                  viewBox={`0 0 ${viewW} ${viewH}`}
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
                  data-testid="qa-cctv-roi"
                >
                  {detail.rois.map((r) => {
                    const pts: string[] = [];
                    for (let i = 0; i + 1 < r.roi.length; i += 2)
                      pts.push(`${r.roi[i]},${r.roi[i + 1]}`);
                    const seen = camera.get(r.parking);
                    const color =
                      seen === undefined
                        ? '#94a3b8'
                        : seen
                          ? SPACE_COLOR.occupied
                          : SPACE_COLOR.empty;
                    const xs = r.roi.filter((_, i) => i % 2 === 0);
                    const ys = r.roi.filter((_, i) => i % 2 === 1);
                    const cx = xs.reduce((a, b) => a + b, 0) / xs.length;
                    const cy = ys.reduce((a, b) => a + b, 0) / ys.length;
                    return (
                      <g key={r.parking}>
                        <polygon
                          points={pts.join(' ')}
                          fill={color}
                          fillOpacity={0.25}
                          stroke={color}
                          strokeWidth={3}
                        />
                        <text
                          x={cx}
                          y={cy}
                          fill="#fff"
                          fontSize={18}
                          fontWeight={700}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          stroke="#000"
                          strokeWidth={4}
                          paintOrder="stroke"
                        >
                          {shortSpaceName(r.parking)}
                        </text>
                      </g>
                    );
                  })}
                </svg>
              )}
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              component="p"
              sx={{ mb: 2 }}
              data-testid="qa-cctv-image-source"
            >
              {detail.liveImage ? (
                <>
                  실시간 판정 이미지 — 영상처리 노드 {detail.liveNode},{' '}
                  {Math.max(0, detail.now - (detail.liveAt ?? detail.now))}초 전. 카메라가 그린 ROI{' '}
                  {detail.rois.length}개와 면별 점유 비율 — 빨간 테두리가 차 있음으로 본 면.
                  새로고침하면 다시 받아 옵니다.
                </>
              ) : (
                <>
                  {detail.image ? '배경 이미지(디워핑)' : '배경 이미지 없음'} 위에 ROI{' '}
                  {detail.rois.length}개 — 카메라가 차 있음으로 본 면은 빨강, 비었음은 초록, 판정
                  없음은 회색. 담당 영상처리 노드의 실시간 판정 이미지는 받지 못했습니다.
                </>
              )}
              {detail.rois.length === 0 && ' cctv_rois 에 이 카메라 ROI 가 없습니다.'}
            </Typography>

            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              연결 주차면 {spaces.length}개
            </Typography>
            <Table
              size="small"
              data-testid="qa-cctv-spaces"
              sx={{ '& th': { whiteSpace: 'nowrap', fontWeight: 700 }, '& td, & th': { px: 1 } }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>주차면</TableCell>
                  <TableCell align="center">DB 점유</TableCell>
                  <TableCell align="center">카메라</TableCell>
                  <TableCell align="center">ROI</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {spaces.map((s) => {
                  const seen = camera.get(s.name);
                  const occupied = s.status === 'Y';
                  const mismatch = seen !== undefined && seen !== occupied;
                  return (
                    <TableRow
                      key={s.id}
                      sx={mismatch ? { bgcolor: (t) => `${t.palette.warning.main}1f` } : undefined}
                    >
                      <TableCell title={s.name} sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                        {shortSpaceName(s.name)}
                        {s.fixed && (
                          <Typography component="span" variant="caption" color="text.secondary">
                            {' '}
                            (고정)
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          clickable
                          disabled={pending.has(s.id)}
                          color={occupied ? 'error' : 'success'}
                          label={occupied ? '점유' : '빈자리'}
                          onClick={() => onToggle(s)}
                          aria-label={`${s.name} ${occupied ? '점유' : '빈자리'}`}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {seen === undefined ? (
                          <Typography variant="caption" color="text.secondary">
                            —
                          </Typography>
                        ) : (
                          <Typography
                            variant="caption"
                            fontWeight={700}
                            color={seen ? 'error.main' : 'success.main'}
                          >
                            {seen ? '차 있음' : '비었음'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Typography
                          variant="caption"
                          color={roiNames.has(s.name) ? 'text.primary' : 'error.main'}
                        >
                          {roiNames.has(s.name) ? '있음' : '없음'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
              DB 점유를 누르면 바뀝니다. DB 점유와 카메라 판정이 다른 줄은 노랗게 표시합니다 —
              카메라 반영 중지 중이면 QA 로 바꾼 값이라 다른 게 정상입니다.
            </Typography>
          </>
        )}
      </DialogContent>
      {fullScreen && (
        <DialogActions sx={{ flexShrink: 0, px: 2, py: 1 }}>
          <Button fullWidth variant="contained" onClick={onClose} sx={{ minHeight: 48 }}>
            CCTV 상세 닫기
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default QaCctvDialog;
